"use client";

import React from "react";
import Image from "next/image";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, Mousewheel } from "swiper/modules";

import { Paragraph } from "@/components/ui/typography";

import useMediaQuery from "@/hooks/use-media-query";
import useMoengageEventTracker from "@/hooks/use-moengage";
import EVENT_NAME from "@/hooks/use-moengage/event-name";
import EVENT_ATTRIBUTE from "@/hooks/use-moengage/event-attribute";

import { fetchExamPreferenceCategories } from "@/fetchers/store";

const bottomBarHeightProperty = "--bottom-bar-height";

function CategoryBottomBar({ show }) {
  const trackMoengageEvent = useMoengageEventTracker();

  const isMatching = useMediaQuery("(max-width: 767px)");
  const [categories, setCategories] = React.useState([]);

  React.useEffect(() => {
    if (!show) {
      document.body.style.removeProperty(bottomBarHeightProperty);
    }
  }, [show]);

  React.useEffect(() => {
    const bottomBarHeight = document.body.style.getPropertyValue(
      bottomBarHeightProperty
    );
    if (bottomBarHeight && isMatching) {
      document.body.style.removeProperty(bottomBarHeightProperty);
    }
  }, [isMatching]);

  const toggleHeaderBottomBarEvent = React.useCallback(() => {
    const bottomBarHeight = document.body.style.getPropertyValue(
      bottomBarHeightProperty
    );
    if (bottomBarHeight) {
      document.body.style.removeProperty(bottomBarHeightProperty);
    } else {
      document.body.style.setProperty(bottomBarHeightProperty, "4.125rem");
    }
  }, []);

  React.useEffect(() => {
    fetchExamPreferenceCategories()
      .then(({ data }) => setCategories(data || []))
      .catch(() => setCategories([]));

    // Event Listner
    window.addEventListener(
      "toggleHeaderBottomBar",
      toggleHeaderBottomBarEvent
    );

    return () =>
      window.removeEventListener(
        "toggleHeaderBottomBar",
        toggleHeaderBottomBarEvent
      );
  }, [toggleHeaderBottomBarEvent]);

  const handleItemClick = (category) => () => {
    const selectGoalItemEvent = new CustomEvent("selectGoalItem", {
      detail: category,
    });
    window.dispatchEvent(selectGoalItemEvent);
    document.body.style.removeProperty(bottomBarHeightProperty);
    document
      .getElementById("popular-online-courses")
      .scrollIntoView({ behavior: "smooth", block: "center" });

    trackMoengageEvent(EVENT_NAME.HOMEPAGE_ALL_COURSE_CLICKED, {
      [EVENT_ATTRIBUTE.GOAL]: category.title,
    });
  };

  return (
    <div
      className="block bg-gray-100 overflow-hidden transition-all duration-300"
      style={{ height: "var(--bottom-bar-height,0)" }}
    >
      <div className="container-studyiq mx-auto">
        <Swiper
          modules={[FreeMode, Mousewheel]}
          slidesPerView="auto"
          spaceBetween={12}
          mousewheel={{ forceToAxis: true }}
          freeMode
        >
          {categories.map((category) => (
            <SwiperSlide
              key={category.id}
              className="shrink! inline-flex! flex-col gap-y-2 items-center py-2.5 px-4 text-sm font-medium text-grayscale-header whitespace-nowrap cursor-pointer"
              onClick={handleItemClick(category)}
            >
              <Image
                src={category.image}
                alt={category.title}
                height={24}
                width={24}
              />
              <Paragraph
                variant="b5-tight"
                className="max-w-18 overflow-hidden overflow-ellipsis"
              >
                {category.title}
              </Paragraph>
            </SwiperSlide>
          ))}
        </Swiper>
      </div>
    </div>
  );
}

export default CategoryBottomBar;
